/**
 * AdminReview — the Admin console (grouped top-level tabs; founder
 * live-testing feedback 2026-07-10 replaced the X.3.7 left rail).
 *
 * One page, a top-level tab per section with its views in a second row:
 *   Directory ▸ Review queue / Add organizations / Duplicates / Categories & tags
 *   People    ▸ Everyone (merged Team + Staff)
 *   Activity  ▸ Change log
 *   AI tools  ▸ Usage / Limits & access / Prompts
 *   Billing   ▸ Rate bands / Rate bands by role / Client rates / QuickBooks
 *
 * Steward-gated: non-stewards see the access-denied panel (the data endpoints
 * 403 regardless; the UI gate avoids an always-403 surface).
 *
 * Views are URL-driven via ?tab= (unchanged keys, so deep-links + the legacy
 * /lookups,/suggestions,/bulk-add redirects keep working). The old 'team' and
 * 'staff' keys normalize to 'people'; the retired 'billing-rules' key lands on
 * 'rate-bands' (its substance moved to the Pre-billing states disclosure +
 * docs/sops/billing-readiness-sop.md). The Change-log view keeps ?scope=.
 *
 * MySuggestions stays a separate page at /t/:tenant/my-suggestions.
 */

import { useMemo, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTenant, canOwn } from '@/lib/auth';
import { canAddLookup } from '@/lib/lookups';
import {
  canArchiveProviders,
  canMergeProviders,
  canManageTeam,
  canViewErrors,
  canManageStaffProfiles,
  canManageClientRates,
  canReadAllSuggestions,
  canManagePermissions,
  canOpenAdminConsole,
  canOpenQuickBooksSettings,
} from '@/lib/capabilities';
import {
  AdminReviewTabs,
  VALID_TABS,
  VIEW_LABELS,
  isAdminTabHidden,
  firstVisibleAdminTab,
  type AdminReviewTab,
} from '@/components/AdminReviewTabs';
import { RecentActivityTab } from '@/components/RecentActivityTab';
import { SuggestionsQueue } from '@/pages/SuggestionsQueue';
import { LookupAdmin } from '@/pages/LookupAdmin';
import { BulkAddOrgs } from '@/pages/BulkAddOrgs';
import { PeopleTab } from '@/components/PeopleTab';
import { MergesTab } from '@/components/MergesTab';
import { AiUsageTab } from '@/components/AiUsageTab';
import { EmailEditorTab } from '@/components/EmailEditorTab';
import { ErrorsTab } from '@/components/ErrorsTab';
import { AiLimitsTab } from '@/components/AiLimitsTab';
import { AiPromptsTab } from '@/components/AiPromptsTab';
import { RateBandsEditorTab } from '@/components/RateBandsEditorTab';
import { NotificationDefaultsTab } from '@/components/NotificationDefaultsTab';
import { UnitCoverageTab } from '@/components/UnitCoverageTab';
import { MapColorsTab } from '@/components/MapColorsTab';
import { SpiceKindColorsTab } from '@/components/SpiceKindColorsTab';
import { RateBandMatrixTab } from '@/components/RateBandMatrixTab';
import { ClientRatesTab } from '@/components/ClientRatesTab';
import { PermissionsTab } from '@/components/PermissionsTab';
import { QuickBooksCard } from '@/components/billing/QuickBooksCard';
import { QboConnectResultBanner } from '@/components/billing/QboConnectResultBanner';
import { PageShell } from '@/components/primitives/PageShell';
import { BackControl } from '@/components/BackControl';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

// Legacy ?tab= keys → canonical keys (Team + Staff merged into People;
// the retired Billing-rules page lands on its Billing-section neighbor).
const LEGACY_TAB_ALIASES: Record<string, AdminReviewTab> = {
  team: 'people',
  staff: 'people',
  'billing-rules': 'rate-bands',
};

function resolveTab(raw: string | null): AdminReviewTab {
  if (raw && raw in LEGACY_TAB_ALIASES) return LEGACY_TAB_ALIASES[raw];
  if (VALID_TABS.includes(raw as AdminReviewTab)) return raw as AdminReviewTab;
  return 'pending';
}

export function AdminReview(): ReactNode {
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const contact = useSupportContact();
  const [searchParams, setSearchParams] = useSearchParams();

  const tenantSlug = routeTenantSlug ?? '';
  const isSteward = canAddLookup(whoami);
  const userCanArchive = canArchiveProviders(whoami);
  const isOwner = canOwn(whoami);
  const userCanMerge = canMergeProviders(whoami);
  // AI-tools views are manage_team-gated server-side; mirror that in the rail so
  // a steward-but-not-manage_team user doesn't see tabs whose endpoints 403.
  const canSeeAiTools = canManageTeam(whoami);
  // Slice 3c — the broader bio/photo editors (admin_support / data_steward /
  // exec_support) reach the People tab (and ONLY the People tab — see below).
  const canEditProfiles = canManageStaffProfiles(whoami);
  // B1-D3c — Billing > Client rates mirrors its endpoint's gate
  // (manage_client_rates: owner + the bookkeeping seats).
  const canSetClientRates = canManageClientRates(whoami);
  // 2026-07-14 — the Review queue + Change log mirror their endpoints' gate
  // (read_all_suggestions: owner + data_steward + the read-only
  // board_observer), split off the steward (manage_lookups) flag.
  const canReadQueue = canReadAllSuggestions(whoami);
  // ADR-0046 — the Permissions tab mirrors its endpoints' gate
  // (manage_permissions: owner-only, hard-locked).
  const canSeePermissions = canManagePermissions(whoami);
  // Billing > QuickBooks holds surfaces with three different server
  // gates, so its admission is the UNION of them: the owner disconnects
  // the company (manage_billing_integration), the billing seats connect
  // or reconnect it (connect_billing_integration) and link clients
  // inside it and map the rate bands (manage_qbo_customer_links).
  const canSeeQuickBooks = canOpenQuickBooksSettings(whoami);
  // ADR-052 — the Errors view mirrors its endpoint's gate (view_errors:
  // the owner and admin support).
  const canSeeErrors = canViewErrors(whoami);

  // Shared per-view visibility, used by the rail, the mobile select, AND the
  // active-tab coercion (single source of truth). People opens to the profile
  // editors; the steward-only Directory write views hide for non-stewards;
  // the queue/activity reads hide for non-read_all_suggestions holders.
  const tabVisibility = {
    hidePeopleTab: !(isOwner || canEditProfiles),
    hideMergesTab: !userCanMerge,
    hideAiToolsTabs: !canSeeAiTools,
    hideStewardTabs: !isSteward,
    hideQueueTabs: !canReadQueue,
    hideClientRatesTab: !canSetClientRates,
    hidePermissionsTab: !canSeePermissions,
    hideQuickBooksTab: !canSeeQuickBooks,
    hideErrorsTab: !canSeeErrors,
  };

  // Coerce to a visible tab: a profiles-only role landing on (or URL-hacking to)
  // a steward tab gets the first visible tab — 'people' — not a 403 shell.
  const rawTab = resolveTab(searchParams.get('tab'));
  const activeTab = isAdminTabHidden(rawTab, tabVisibility) ? firstVisibleAdminTab(tabVisibility) : rawTab;

  const scopeParam = searchParams.get('scope');
  const scope: 'all' | 'mine' = scopeParam === 'mine' ? 'mine' : 'all';

  // Review-queue count badge — see AppShell badge cache (Phase 1 pragmatic).
  const pendingCount = useMemo<number | null>(() => null, []);

  function handleTabChange(next: AdminReviewTab): void {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    // Drop scope when leaving the Change log (avoid a stale URL).
    if (next !== 'activity') sp.delete('scope');
    setSearchParams(sp, { replace: true });
  }

  function handleScopeChange(next: 'all' | 'mine'): void {
    const sp = new URLSearchParams(searchParams);
    if (next === 'mine') sp.set('scope', 'mine');
    else sp.delete('scope');
    setSearchParams(sp, { replace: true });
  }

  // UX-12: the shell renders the Console eyebrow + Admin title on both
  // branches; the gate is a prop resolved below all hooks (the locked
  // contract), with the page's richer denial surface via deniedContent.
  const deniedContent = (
    <>
      <div
        role="alert"
        className="my-3 p-3 border border-warm-300 bg-warm-50 rounded-card text-sm text-ink"
      >
        {contactSentence(
          "This page is for the people who look after the Directory. Ask {contact_admin} if your work needs it.",
          contact,
        )}
      </div>
      <BackControl
        label="Home"
        ariaLabel="Back to home"
        to={`/t/${encodeURIComponent(tenantSlug)}/hub`}
        testId="back-to-home"
        className="mt-4"
      />
    </>
  );

  return (
    <PageShell
      eyebrow="Console"
      title="Admin"
      maxWidth="xl"
      access={{
        // canOpenAdminConsole is the shared admission predicate, also the
        // 'admin-review' nav gate; equivalent to isSteward ||
        // canEditProfiles || canReadQueue || canSeePermissions. Widen it
        // in capabilities.ts, never inline here.
        allowed: canOpenAdminConsole(whoami),
        deniedContent,
      }}
    >
      <div className="flex flex-col gap-5">
        {/* The console's way back: where the person came from when Olive
            knows it (the page behind this one), else Home, its parent. */}
        <div className="-mb-9">
          <BackControl
            label="Home"
            ariaLabel="Back to home"
            to={`/t/${encodeURIComponent(tenantSlug)}/hub`}
            testId="back-to-home"
            className="mb-4"
          />
        </div>
        {/* The section tab rows at md and up, and the same views as a
            native select below md (both rendered by AdminReviewTabs). */}
        <AdminReviewTabs
          activeTab={activeTab}
          pendingCount={pendingCount}
          {...tabVisibility}
          onChange={handleTabChange}
        />

        <section
          id="admin-tabpanel"
          role="tabpanel"
          tabIndex={0}
          aria-label={VIEW_LABELS[activeTab]}
          className="min-w-0 w-full focus-visible:outline-none"
        >
          {activeTab === 'pending' && <SuggestionsQueue />}

          {activeTab === 'activity' && (
            <RecentActivityTab
              tenantSlug={tenantSlug}
              scope={scope}
              onScopeChange={handleScopeChange}
              canArchive={userCanArchive}
            />
          )}

          {activeTab === 'bulk' && <BulkAddOrgs />}

          {activeTab === 'lookups' && <LookupAdmin />}

          {activeTab === 'people' && <PeopleTab />}

          {activeTab === 'merges' && <MergesTab tenantSlug={tenantSlug} />}

          {activeTab === 'errors' && <ErrorsTab tenantSlug={tenantSlug} />}

          {activeTab === 'ai-usage' && <AiUsageTab tenantSlug={tenantSlug} />}

          {activeTab === 'ai-limits' && <AiLimitsTab tenantSlug={tenantSlug} />}

          {activeTab === 'ai-prompts' && <AiPromptsTab tenantSlug={tenantSlug} />}
          {activeTab === 'email-editor' && <EmailEditorTab tenantSlug={tenantSlug} />}

          {activeTab === 'rate-band-editor' && <RateBandsEditorTab tenantSlug={tenantSlug} />}

          {activeTab === 'rate-bands' && <RateBandMatrixTab tenantSlug={tenantSlug} />}

          {activeTab === 'client-rates' && <ClientRatesTab tenantSlug={tenantSlug} />}

          {/* The QuickBooks settings: the connection and its actions, the
              customer-link ceremony, and the rate-band item map. The card
              reads its tenant from context, so it needs no props here.

              The banner leads it because Connect is pressed on this view
              while the OAuth callback redirects to the app root, not back
              here: the outcome rides sessionStorage and surfaces wherever
              the banner is mounted. Invoices mounts one too; they are
              different routes, and the first to show a token spends it. */}
          {activeTab === 'quickbooks' && (
            <>
              <QboConnectResultBanner />
              <QuickBooksCard />
            </>
          )}

          {activeTab === 'permissions' && <PermissionsTab tenantSlug={tenantSlug} />}

          {activeTab === 'notification-defaults' && (
            <NotificationDefaultsTab tenantSlug={tenantSlug} />
          )}

          {activeTab === 'unit-coverage' && <UnitCoverageTab tenantSlug={tenantSlug} />}

          {activeTab === 'map-colours' && <MapColorsTab tenantSlug={tenantSlug} />}
          {activeTab === 'spice-colours' && <SpiceKindColorsTab tenantSlug={tenantSlug} />}
        </section>
      </div>
    </PageShell>
  );
}
